import type { SourceDto, SourceItemDto, SourcesResponse } from '@zinc/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../lib/api';
import { SCHEDULE_LABELS } from './AdminOverviewPage';
import { Panel, SmallSwitch, smallButton, tableCell, tinyButton, when } from './parts';

const STATE_LABELS: Record<string, string> = {
  imported: 'Imported',
  'no-puzzle': 'No puzzle',
  'image-only': 'Image only (v2)',
  failed: 'Failed',
  waiting: 'Waiting for a rating',
  skipped: 'Skipped',
  queued: 'Queued',
};

function Items({ source }: { source: SourceDto }) {
  const items = useQuery({
    queryKey: ['admin', 'source-items', source.id],
    queryFn: () => api<{ items: SourceItemDto[] }>(`/api/admin/sources/${source.id}/items`),
  });
  return (
    <div className="flex flex-col gap-1 text-[13px]">
      {(items.data?.items ?? []).map((i) => (
        <div key={i.id} className="flex items-center gap-3 border-b border-line-soft py-1">
          <span className="w-16 shrink-0 text-muted">{when(i.createdAt)}</span>
          <a href={i.url} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate">
            {i.title || i.url}
          </a>
          <span
            className={i.state === 'failed' ? 'text-mistake' : 'text-muted'}
            title={i.error ?? ''}
          >
            {STATE_LABELS[i.state] ?? i.state}
            {i.puzzleIds.length > 0 && ` · ${i.puzzleIds.length}`}
          </span>
          {i.puzzleIds[0] && (
            <Link to={`/puzzle/${i.puzzleIds[0]}`} className="shrink-0">
              Open
            </Link>
          )}
        </div>
      ))}
      {items.data?.items.length === 0 && <span className="text-muted">Nothing found yet.</span>}
    </div>
  );
}

/** Admin → Sources: add by URL, switch off, set the schedule, run now; what each found. */
export function AdminSourcesPage() {
  const client = useQueryClient();
  const sources = useQuery({
    queryKey: ['admin', 'sources'],
    queryFn: () => api<SourcesResponse>('/api/admin/sources'),
    refetchInterval: 10_000,
  });
  const [url, setUrl] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const refresh = () => void client.invalidateQueries({ queryKey: ['admin'] });
  const add = useMutation({
    mutationFn: (u: string) =>
      api<SourceDto>('/api/admin/sources', { method: 'POST', json: { url: u } }),
    onSuccess: () => {
      setUrl('');
      refresh();
    },
  });
  const patch = useMutation({
    mutationFn: (v: { id: string; enabled?: boolean; schedule?: string }) =>
      api(`/api/admin/sources/${v.id}`, {
        method: 'PATCH',
        json: { enabled: v.enabled, schedule: v.schedule },
      }),
    onSuccess: refresh,
  });
  const run = useMutation({
    mutationFn: (id: string) => api(`/api/admin/sources/${id}/run`, { method: 'POST' }),
    onSuccess: refresh,
  });
  return (
    <>
      <h1 className="m-0 text-[26px] font-bold">Sources</h1>
      <Panel title="Add a source">
        <form
          className="flex flex-wrap gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (url.trim()) add.mutate(url.trim());
          }}
        >
          <label htmlFor="source-url" className="sr-only">
            Source link
          </label>
          <input
            id="source-url"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="A YouTube channel (/@name) or a feed (Substack, a blog's RSS)"
            className="h-10 min-w-0 flex-1 rounded-btn border border-line bg-surface px-3 font-sans text-[14px] text-ink"
          />
          <button type="submit" disabled={add.isPending || !url.trim()} className={smallButton}>
            Add source
          </button>
        </form>
        {add.isError && (
          <p role="alert" className="m-0 text-[13px] text-mistake">
            {add.error.message}
          </p>
        )}
      </Panel>
      <Panel title="All sources">
        <div className="-mx-1 overflow-x-auto px-1">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="text-left text-muted">
                <th className={`${tableCell} font-semibold`}>Source</th>
                <th className={`${tableCell} font-semibold`}>Checked</th>
                <th className={`${tableCell} font-semibold`}>Last run</th>
                <th className={`${tableCell} text-right font-semibold`}>Puzzles</th>
                <th className={`${tableCell} font-semibold`} />
                <th className={`${tableCell} text-right font-semibold`}>On</th>
              </tr>
            </thead>
            <tbody>
              {(sources.data?.sources ?? []).map((s) => (
                <tr key={s.id} className="align-top">
                  <td className={tableCell}>
                    <button
                      type="button"
                      onClick={() => setOpen(open === s.id ? null : s.id)}
                      className="border-0 bg-transparent p-0 text-left text-[13px] font-semibold text-ink"
                      aria-expanded={open === s.id}
                    >
                      {s.name}
                    </button>
                    <div className="text-[12px] text-muted">
                      {s.waiting > 0 && `${s.waiting} waiting · `}
                      {s.imageOnly > 0 && `${s.imageOnly} image only · `}
                      <a href={s.url} target="_blank" rel="noreferrer">
                        {new URL(s.url).host}
                      </a>
                    </div>
                    {open === s.id && <Items source={s} />}
                  </td>
                  <td className={tableCell}>
                    <select
                      aria-label={`${s.name} schedule`}
                      value={s.schedule}
                      onChange={(e) => patch.mutate({ id: s.id, schedule: e.target.value })}
                      className="h-8 rounded-[8px] border border-line bg-surface font-sans text-[13px] text-ink"
                    >
                      {Object.entries(SCHEDULE_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className={tableCell}>
                    <div>{when(s.lastRunAt)}</div>
                    {s.lastError && (
                      <div className="max-w-[260px] text-[12px] font-semibold text-[#A3212D]">
                        {s.lastError}
                      </div>
                    )}
                  </td>
                  <td className={`${tableCell} text-right tabular-nums`}>{s.puzzles}</td>
                  <td className={tableCell}>
                    <button type="button" className={tinyButton} onClick={() => run.mutate(s.id)}>
                      Run now
                    </button>
                  </td>
                  <td className={`${tableCell} text-right`}>
                    <SmallSwitch
                      checked={s.enabled}
                      label={`${s.name} on`}
                      onChange={(enabled) => patch.mutate({ id: s.id, enabled })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </>
  );
}
